import { generateGuid } from "@1js/guid";
import type { Prompt } from "@1js/pl-card-components";
import type { Group } from "@1js/pl-types";
import type { SiteSetting } from "../types";
import { sendResponseReceivedEvent } from "../utils/Telemetry";
import { authAwareFetch, retry } from "./fetch";

/**
 * Response header cue used to tell if the response is from 3S Suggestions API or not.
 */
export const Is3SResponseHeader = "x-datasource-is3s";

/**
 * Response header cue used to tell if the current user has proper Copilot license.
 */
export const IsUserLicensed = "x-licensed";

/**
 * Logical ID header key used to identify 3s API calls.
 * See: https://docs.substrate.microsoft.net/docs/Substrate-Intelligence/MSAI-Data-Platform/Contracts/SearchInstrumentationOverview/ContractOverview.html
 */
export const LogicalIdHeaderKey = "x-logical-id";

export type PromptReference = {
  PromptId: string;
};

export type PostPromptsPublishResponse = {
  Prompts: PromptReference[];
};

export type DeletePromptsPublishResponse = {
  Prompts: PromptReference[];
};

export type GetPromptsPublishResponse = {
  PromptList: Prompt[];
  PromptListCount: number;
};

export type GetPromptsWorkgroupsResponse = {
  Workgroups: Group[];
};

export const searchAsync = async (
  host: string,
  locale: string,
  path: string
): Promise<string> => {
  const logicalId = generateGuid();
  const serviceUrl = locale
    ? `https://${host}/${locale}/${path}`
    : `https://${host}/${path}`;
  const response = await retry(() =>
    authAwareFetch(serviceUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        [LogicalIdHeaderKey]: logicalId,
      },
    })
  );

  return response.json();
};

/**
 * Calls SupportService endpoint to get group prompts.
 *
 * @param host service host
 * @param locale requested locale
 * @returns Returns prompts, enriched with group information.
 */
export const fetchGroupPrompts = async (
  host: string,
  locale: string,
  groupId: string,
  groups?: Group[]
): Promise<GetPromptsPublishResponse> => {
  if (!groups) {
    return { PromptList: [], PromptListCount: 0 };
  }

  const logicalId = generateGuid();
  const serviceUrl = `https://${host}/${locale}/api/promptsclient/prompts/publish?workgroupId=${groupId}`;

  const response = await retry(() =>
    authAwareFetch(serviceUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        [LogicalIdHeaderKey]: logicalId,
      },
    })
  );

  const parsedResponse = (await response.json()) as GetPromptsPublishResponse;
  parsedResponse.PromptList = parsedResponse.PromptList.map(
    (prompt: Prompt) => {
      return {
        ...prompt,
        LogicalId: logicalId,
        Groups: [groups.filter((group) => group.Id == groupId)[0]],
      };
    }
  );

  return parsedResponse;
};

/**
 * Calls 3s suggestions endpoint to get prompts for the given query.
 *
 * @param host service host
 * @param locale requested locale
 * @param is3SServiceEnabled optional is3SServiceEnabled flag
 * @returns Returns a tuple of [Prompt[], boolean] where the boolean indicates if the response is from 3S or not.
 */
export const fetchAllPrompts = async (
  host: string,
  locale: string,
  is3SServiceEnabled?: boolean
): Promise<[Prompt[], boolean, boolean]> => {
  const logicalId = generateGuid();
  let serviceUrl = `https://${host}/${locale}/api/promptsclient/allPrompts`;
  if (is3SServiceEnabled === false) {
    // 3s endpoint is always enabled, only attach the ?is3SServiceEnabled querystring if the value is false
    serviceUrl += "?is3SServiceEnabled=false";
  }

  const response = await retry(() =>
    authAwareFetch(serviceUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        [LogicalIdHeaderKey]: logicalId,
      },
    })
  );

  sendResponseReceivedEvent(
    parseInt(response.headers.get("x-datasource-latency") ?? "0", 10),
    parseInt(response.headers.get("x-datasource-statuscode") ?? "0", 10),
    response.headers.get("x-datasource-traceid") ?? ""
  );

  let prompts = (await response.json()) as Prompt[];
  prompts = prompts.map((prompt) => {
    return {
      ...prompt,
      LogicalId: logicalId,
    };
  });

  return [
    prompts,
    response.headers.get(Is3SResponseHeader)?.toLowerCase() === "true",
    response.headers.get(IsUserLicensed)?.toLowerCase() === "true",
  ];
};

/**
 * Calls MS Graph via SupportService to get groups for the given user.
 *
 * @param host service host
 * @param locale requested locale
 * @returns Returns a Group-array
 */
export const fetchGroups = async (
  host: string,
  locale: string
): Promise<GetPromptsWorkgroupsResponse> => {
  const logicalId = generateGuid();
  const serviceUrl = `https://${host}/${locale}/api/promptsclient/prompts/workgroups?workgroupType=all`;

  const response = await retry(() =>
    authAwareFetch(serviceUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        [LogicalIdHeaderKey]: logicalId,
      },
    })
  );

  const groups = (await response.json()) as GetPromptsWorkgroupsResponse;
  groups.Workgroups.forEach((group) => {
    group.IsMember = true;
  });

  return groups;
};

/**
 * Fetches saved prompts from the server.
 * @param host service host
 * @param user current logged in user
 * @returns saved prompts
 */
export const fetchSavedPrompts = async (
  host: string,
  user: NonNullable<SiteSetting["user"]>
): Promise<Prompt[]> => {
  const logicalId = generateGuid();
  const serviceUrl = `https://${host}/api/promptsclient/saved`;
  const avatarUrl = `https://${host}/api/graph/users/${user.objectId}/photos/48x48/$value`;

  const response = await retry(() =>
    authAwareFetch(serviceUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        [LogicalIdHeaderKey]: logicalId,
      },
    })
  );

  sendResponseReceivedEvent(
    parseInt(response.headers.get("x-datasource-latency") ?? "0", 10),
    parseInt(response.headers.get("x-datasource-statuscode") ?? "0", 10),
    response.headers.get("x-datasource-traceid") ?? ""
  );

  const payload = (await response.json()) as {
    PromptList: Prompt[];
    PromptListCount: number;
  };
  let prompts = payload["PromptList"] as Prompt[];
  prompts = prompts.map((prompt) => {
    return {
      ...prompt,
      IsUserPrompt: true,
      DisplayCategory: "Created by Me",
      Author: {
        DisplayName: user.displayName,
        Email: user.email,
        Avatar: avatarUrl,
      },
      LogicalId: logicalId,
    };
  });

  return prompts;
};

/**
 * Deletes a saved prompt from the server.
 * @param host service host
 * @param promptId prompt id to delete
 */
export const deleteSavedPrompt = async (
  host: string,
  promptId: string
): Promise<void> => {
  const serviceUrl = `https://${host}/api/promptsclient/saved`;
  await retry(() =>
    authAwareFetch(serviceUrl, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        RequestVerificationToken:
          document
            ?.querySelector('input[name="__RequestVerificationToken"]')
            ?.getAttribute("value") ?? "",
      },
      body: JSON.stringify({
        promptId,
      }),
    })
  );
};

/**
 * Deletes a shared prompt from the workgroup.
 * @param host service host
 * @param promptId prompt id to delete from groupId
 * @param groupId group id to delete prompt from
 */
export const deleteSharedPromptFromWorkgroup = async (
  host: string,
  prompts: Prompt[],
  groupIds: string[]
): Promise<DeletePromptsPublishResponse> => {
  const serviceUrl = `https://${host}/api/promptsclient/prompts/publish`;
  const response: Response = await retry(() =>
    authAwareFetch(serviceUrl, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        RequestVerificationToken:
          document
            ?.querySelector('input[name="__RequestVerificationToken"]')
            ?.getAttribute("value") ?? "",
      },
      body: JSON.stringify({
        Prompts: prompts,
        Workgroups: groupIds.map((groupId) => ({ Id: groupId })),
      }),
    })
  );

  return response.json();
};

/**
 * Shares a prompt with a workgroup.
 * @param host service host
 * @param prompts prompts to share
 * @param groupIds group ids to share with
 */
export const sharePromptToWorkgroup = async (
  host: string,
  prompts: Prompt[],
  groupIds: string[]
): Promise<PostPromptsPublishResponse> => {
  const serviceUrl = `https://${host}/api/promptsclient/prompts/publish`;
  const response: Response = await retry(() =>
    authAwareFetch(serviceUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        RequestVerificationToken:
          document
            ?.querySelector('input[name="__RequestVerificationToken"]')
            ?.getAttribute("value") ?? "",
      },
      body: JSON.stringify({
        Prompts: prompts,
        Workgroups: groupIds.map((groupId) => ({ Id: groupId })),
      }),
    })
  );

  return response.json();
};

export const fetchBookmarkedPrompts = async (
  host: string,
  locale: string
): Promise<Prompt[]> => {
  const logicalId = generateGuid();
  const serviceUrl = `https://${host}/${locale}/api/promptsclient/bookmarkedPrompts`;
  const response = await retry(() =>
    authAwareFetch(serviceUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        [LogicalIdHeaderKey]: logicalId,
      },
    })
  );

  return response.json();
};

export const addFavoriteAsync = async (
  host: string,
  promptId: string
): Promise<void> => {
  const serviceUrl = `https://${host}/api/promptsclient/favorites/add?promptId=${promptId}`;
  await retry(() =>
    authAwareFetch(serviceUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
  );
};

export const removeFavoriteAsync = async (
  host: string,
  promptId: string
): Promise<void> => {
  const serviceUrl = `https://${host}/api/promptsclient/favorites/remove?promptId=${promptId}`;
  await retry(() =>
    authAwareFetch(serviceUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
  );
};
